import { useState } from 'react';
import { Link } from 'react-router';
import { CardActions, Lightbox, absoluteUrl, type LightboxContent } from './ResourceActions';
import { onImageError } from '@/lib/imageFallback';

const HERO_BG = '/landing/video/video-hero.webp';

/* ── Dark hero with search ────────────────────────────────────────────── */

export function ResourceHero({
    titleLead,
    titleBold,
    intro,
    search = true,
    query,
    onSearch,
    searchPlaceholder = 'Search',
}: {
    titleLead?: string;
    titleBold: string;
    intro: string;
    /** The court-ranking page filters by state and year instead, so it hides the search bar. */
    search?: boolean;
    /** Wire both to make the box filter the page; without them it is the design's placeholder. */
    query?: string;
    onSearch?: (query: string) => void;
    searchPlaceholder?: string;
}) {
    return (
        <section className="relative overflow-hidden bg-[#0a0a0a]">
            <img
                src={HERO_BG}
                alt=""
                className="absolute inset-0 size-full object-cover grayscale"
                style={{ objectPosition: '50% 25%' }}
            />
            <div className="absolute inset-0 bg-black/65" />

            <div className="relative mx-auto max-w-[1640px] px-5 pb-12 pt-[130px] lg:px-[140px] lg:pb-16 lg:pt-[170px]">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:gap-14">
                    <h1 className="font-primary text-[46px] font-bold leading-[1.05] tracking-[-2px] text-white lg:text-[72px] lg:tracking-[-3px]">
                        {titleLead && <span className="text-white/45">{titleLead} </span>}
                        {titleBold}
                        <span className="text-[#f60]">.</span>
                    </h1>
                    <p className="max-w-[460px] text-[13px] leading-[1.7] text-white/80 lg:mb-4 lg:text-[14px]">
                        {intro}
                    </p>
                </div>

                {/* Search */}
                {search && (
                    <form
                        role="search"
                        className="mt-8 flex max-w-[560px] flex-col gap-3 sm:flex-row"
                        onSubmit={(e) => e.preventDefault()}
                    >
                        <label className="relative flex-1">
                            <span className="sr-only">Search</span>
                            <input
                                type="search"
                                placeholder={searchPlaceholder}
                                value={onSearch ? (query ?? '') : undefined}
                                onChange={onSearch ? (e) => onSearch(e.target.value) : undefined}
                                className="h-[42px] w-full rounded-[6px] bg-white/95 px-4 pr-10 text-[14px] text-[#151515] outline-none placeholder:text-[#8a8a8a]"
                            />
                            <svg
                                className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-[#8a8a8a]"
                                viewBox="0 0 24 24"
                                fill="none"
                                aria-hidden
                            >
                                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </label>
                        <button
                            type="submit"
                            className="h-[42px] shrink-0 rounded-[6px] bg-[#f60] px-8 text-[14px] font-bold text-white sm:w-[130px]"
                        >
                            Search
                        </button>
                    </form>
                )}
            </div>
        </section>
    );
}

/* ── Small orange-dot section label ───────────────────────────────────── */

export function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
    return (
        <div className={`flex items-center gap-2 ${className ?? ''}`}>
            <span className="size-[6px] rounded-[1px] bg-[#f60]" />
            <p className="text-[15px] font-bold text-[#08080c]">{children}</p>
        </div>
    );
}

/* ── Pagination ───────────────────────────────────────────────────────── */

const PAGE_WINDOW = 5;

/** Up to five page numbers around the current one. */
function pageWindow(page: number, pages: number): number[] {
    const start = Math.max(1, Math.min(page - Math.floor(PAGE_WINDOW / 2), pages - PAGE_WINDOW + 1));
    return Array.from({ length: Math.min(PAGE_WINDOW, pages) }, (_, i) => start + i);
}

/**
 * Page controls. With `onChange` it drives the list it sits under — see
 * `usePagedList`. Without it, it is still the design's placeholder for the
 * pages whose content has not moved to the CMS yet.
 */
export function Pagination({
    page: controlledPage,
    pages = 26,
    onChange,
}: {
    page?: number;
    pages?: number;
    onChange?: (page: number) => void;
}) {
    const [localPage, setLocalPage] = useState(3);
    const live = !!onChange;
    const page = live ? Math.min(Math.max(controlledPage ?? 1, 1), Math.max(pages, 1)) : localPage;
    const go = (next: number) => {
        const clamped = Math.min(Math.max(next, 1), Math.max(pages, 1));
        if (live) onChange(clamped);
        else setLocalPage(clamped);
    };
    const shown = live ? pageWindow(page, pages) : [1, 2, 3, 4, 5];
    const showLast = live ? pages > 0 && !shown.includes(pages) : true;

    if (live && pages <= 1) return null;

    return (
        <nav aria-label="Pagination" className="mt-10 flex items-center justify-center gap-2">
            <button
                type="button"
                aria-label="Previous page"
                disabled={live && page <= 1}
                onClick={() => go(page - 1)}
                className="flex size-8 items-center justify-center rounded-full border border-[#e0e0e0] text-[#5e5e5e] disabled:opacity-40"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
            </button>
            {shown.map((n) => (
                <button
                    key={n}
                    type="button"
                    onClick={() => go(n)}
                    aria-current={n === page ? 'page' : undefined}
                    className={`flex size-8 items-center justify-center rounded-full text-[13px] font-medium ${
                        n === page ? 'bg-[#f60] text-white' : 'text-[#5e5e5e] hover:bg-[#f0f0f0]'
                    }`}
                >
                    {n}
                </button>
            ))}
            {showLast && (
                <button
                    type="button"
                    onClick={() => go(pages)}
                    aria-label={`Last page, ${pages}`}
                    className="px-1 text-[13px] text-[#5e5e5e] hover:underline"
                >
                    {pages}
                </button>
            )}
            <button
                type="button"
                aria-label="Next page"
                disabled={live && page >= pages}
                onClick={() => go(page + 1)}
                className="flex size-8 items-center justify-center rounded-full border border-[#e0e0e0] text-[#5e5e5e] disabled:opacity-40"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
            </button>
        </nav>
    );
}

/* ── Download card (publications / infographics / reports) ────────────── */

export interface DownloadItem {
    cover: string;
    text: string;
    /** Used to build the shareable link back to this item. */
    slug?: string;
    /** The file a download hands over. Falls back to the cover image. */
    file?: string;
}

export function DownloadCard({
    item,
    basePath,
    onView,
}: {
    item: DownloadItem;
    basePath: string;
    onView: (item: DownloadItem) => void;
}) {
    return (
        <article className="flex flex-col rounded-[8px] bg-[#f5f5f5] p-4">
            <button
                type="button"
                onClick={() => onView(item)}
                aria-label={`View ${item.text}`}
                className="h-[170px] w-full overflow-hidden rounded-[4px] bg-white"
            >
                {/* Fitted, not cropped: these covers are a mix of portrait page scans
                    and landscape feature images, and this box is neither shape. */}
                <img
                    src={item.cover}
                    alt=""
                    className="size-full object-contain transition-transform duration-500 hover:scale-[1.05]"
                />
            </button>
            <p className="mt-4 text-[13px] font-semibold leading-[1.6] text-[#08080c]">{item.text}</p>
            <div className="mt-auto">
                <CardActions
                    title={item.text}
                    shareUrl={absoluteUrl(item.slug ? `${basePath}#${item.slug}` : basePath)}
                    onView={() => onView(item)}
                    downloadHref={item.file ?? item.cover}
                />
            </div>
        </article>
    );
}

/**
 * The card grid shared by Publications, Reports and Infographics. It owns the
 * lightbox so a page only has to hand over its items.
 */
export function DownloadGrid({ items, basePath }: { items: DownloadItem[]; basePath: string }) {
    const [preview, setPreview] = useState<LightboxContent | null>(null);

    return (
        <>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {items.map((d, i) => (
                    <DownloadCard
                        key={d.slug ?? i}
                        item={d}
                        basePath={basePath}
                        onView={(item) => setPreview({ title: item.text, image: item.cover, file: item.file })}
                    />
                ))}
            </div>
            <Lightbox item={preview} onClose={() => setPreview(null)} />
        </>
    );
}

/* ── Article card (blog / reports) ────────────────────────────────────── */

export interface ArticleItem {
    slug: string;
    image: string;
    /**
     * Bundled artwork to swap in if `image` cannot be loaded. Set when `image`
     * points at a remote host we do not control — see `hydrateGavelStories`.
     */
    fallbackImage?: string;
    title: string;
    author: string;
    avatar: string;
    /** Standfirst shown in the "Recent" rail; the grid cards show the title only. */
    excerpt?: string;
    /** Article body, one string per paragraph. Read by the article detail page. */
    body?: string[];
    /**
     * The body as sanitised HTML — headings, lists, quotes, links, images —
     * when the CMS has it. Preferred over `body` on the article page.
     */
    bodyHtml?: string;
    /** `11 Sep 2026`, as the CMS shows it. */
    date?: string;
    readingMinutes?: number;
    tags?: string[];
    /** Where it was first published, when it came from Medium. */
    originalUrl?: string;
    /**
     * An external destination instead of a page on this site — a document's
     * PDF. Cards open it in a new tab rather than routing to `basePath/slug`.
     */
    href?: string;
    /** Pull quote rendered part-way through the body on the detail page. */
    quote?: string;
}

/** A card's link: an external file opens in a new tab; anything else routes within the site. */
function CardLink({
    item,
    basePath,
    className,
    children,
    ...rest
}: {
    item: ArticleItem;
    basePath: string;
    className?: string;
    children: React.ReactNode;
} & Record<string, unknown>) {
    if (item.href) {
        return (
            <a href={item.href} target="_blank" rel="noopener noreferrer" className={className} {...rest}>
                {children}
            </a>
        );
    }
    return (
        <Link to={`${basePath}/${item.slug}`} className={className} {...rest}>
            {children}
        </Link>
    );
}

export function ArticleCard({ item, basePath = '/resources/blog' }: { item: ArticleItem; basePath?: string }) {
    return (
        <article className="flex flex-col rounded-[8px] bg-[#f5f5f5] p-4">
            <div className="h-[170px] w-full overflow-hidden rounded-[4px] bg-[#d9d9d9]">
                <img
                    src={item.image}
                    alt=""
                    onError={onImageError(item.fallbackImage)}
                    className="size-full object-cover"
                />
            </div>
            <p className="mt-4 text-[13px] font-semibold leading-[1.6] text-[#08080c]">{item.title}</p>
            <div className="mt-4 flex items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                    <img src={item.avatar} alt="" className="size-6 rounded-full object-cover" />
                    <span className="text-[11px] text-[#5e5e5e]">By {item.author}</span>
                </span>
                <CardLink
                    item={item}
                    basePath={basePath}
                    aria-label={`Read ${item.title}`}
                    className="flex size-8 shrink-0 items-center justify-center rounded-full border border-[#d0d0d0] text-[#08080c] transition-colors hover:bg-[#08080c] hover:text-white"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path
                            d="M5 12h13M12 5l7 7-7 7"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </CardLink>
            </div>
        </article>
    );
}

/* ── Top + Recent split ───────────────────────────────────────────────── */

/** Section label with the orange tick and a rule running to the end of the column. */
function RailHeading({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex items-center gap-3">
            <span className="size-[7px] shrink-0 rounded-[1px] bg-[#f60]" />
            <p className="shrink-0 text-[19px] font-semibold tracking-[-0.4px] text-[#08080c] lg:text-[21px]">
                {children}
            </p>
            <span className="h-px min-w-0 flex-1 bg-[#e2e2e2]" />
        </div>
    );
}

/**
 * The "Top Article / Recent Articles" split that opens the Blog, Reports and
 * Infographics pages: one hero card on the left, a stacked rail on the right.
 */
/**
 * The panel under a list with nothing in it. A search that found nothing and a
 * pool the CMS has not filled — or could not be reached for — are different
 * things, and "Nothing matches “”" for the second reads like a fault.
 */
export function EmptyList({
    query,
    whenSearching,
    whenEmpty = 'Nothing to show here just yet.',
}: {
    query: string;
    whenSearching: string;
    whenEmpty?: string;
}) {
    return (
        <p className="mt-8 rounded-[10px] bg-[#f6f6f6] px-6 py-12 text-center text-[14px] text-[#5e5e5e]">
            {query.trim() ? whenSearching : whenEmpty}
        </p>
    );
}

export function TopAndRecent({
    top,
    recent,
    topLabel = 'Top Article',
    recentLabel = 'Recent Articles',
    basePath = '/resources/blog',
}: {
    top: ArticleItem;
    recent: ArticleItem[];
    topLabel?: string;
    recentLabel?: string;
    basePath?: string;
}) {
    return (
        <section className="bg-white pb-12 pt-12 lg:pb-16 lg:pt-16">
            <div className="mx-auto flex max-w-[1640px] flex-col gap-10 px-5 lg:flex-row lg:gap-14 lg:px-[140px]">
                {/* Top article */}
                <div className="lg:w-[55%] lg:shrink-0">
                    <RailHeading>{topLabel}</RailHeading>

                    <CardLink
                        item={top}
                        basePath={basePath}
                        className="group relative mt-6 block h-[300px] overflow-hidden rounded-[14px] bg-[#111] lg:h-[420px]"
                    >
                        <img
                            src={top.image}
                            onError={onImageError(top.fallbackImage)}
                            alt=""
                            className="absolute inset-0 size-full object-cover grayscale transition-transform duration-700 group-hover:scale-[1.04]"
                        />
                        <span className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />

                        <span className="absolute inset-x-6 bottom-6 flex items-end justify-between gap-6 lg:inset-x-8 lg:bottom-8">
                            <span className="min-w-0">
                                <span className="block max-w-[420px] text-[20px] font-medium leading-[1.35] text-white lg:text-[26px]">
                                    {top.title}
                                </span>
                                <span className="mt-4 flex items-center gap-2.5">
                                    <img src={top.avatar} alt="" className="size-7 rounded-full object-cover" />
                                    <span className="text-[13px] text-white/85">
                                        By <span className="font-bold text-white">{top.author}</span>
                                    </span>
                                </span>
                            </span>

                            <span className="flex size-[52px] shrink-0 items-center justify-center rounded-full bg-[#f60] text-white transition-transform duration-300 group-hover:scale-[1.08] lg:size-[58px]">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                                    <path
                                        d="M7 17L17 7M17 7H8M17 7V16"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                        </span>
                    </CardLink>
                </div>

                {/* Recent rail */}
                <div className="min-w-0 lg:flex-1">
                    <RailHeading>{recentLabel}</RailHeading>

                    <div className="mt-6 flex flex-col gap-7">
                        {recent.map((item) => (
                            <article key={item.slug} className="flex gap-4 lg:gap-5">
                                <CardLink
                                    item={item}
                                    basePath={basePath}
                                    className="h-[86px] w-[110px] shrink-0 overflow-hidden rounded-[6px] bg-[#d9d9d9] lg:h-[96px] lg:w-[128px]"
                                >
                                    <img
                                        src={item.image}
                                        alt=""
                                        onError={onImageError(item.fallbackImage)}
                                        className="size-full object-cover"
                                    />
                                </CardLink>

                                <div className="min-w-0">
                                    <CardLink
                                        item={item}
                                        basePath={basePath}
                                        className="block text-[14px] font-bold leading-[1.45] text-[#08080c] lg:text-[15px]"
                                    >
                                        {item.title}
                                    </CardLink>
                                    {item.excerpt && (
                                        <p className="mt-1.5 text-[12px] leading-[1.6] text-[#5e5e5e]">
                                            {item.excerpt}
                                        </p>
                                    )}
                                    <span className="mt-2.5 flex items-center gap-2">
                                        <img src={item.avatar} alt="" className="size-6 rounded-full object-cover" />
                                        <span className="text-[12px] text-[#5e5e5e]">
                                            By <span className="font-bold text-[#08080c]">{item.author}</span>
                                        </span>
                                    </span>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ── Gallery ──────────────────────────────────────────────────────────── */

export interface GalleryItem {
    slug: string;
    kind: 'video' | 'image';
    /** Still for an image, poster frame for a video. */
    image: string;
    title: string;
    caption?: string;
    /** Embed URL, videos only. */
    videoUrl?: string;
    /** Runtime, shown on video cards. */
    duration?: string;
    /** On the homepage: the film band (videos) or the four frames beneath it (images). */
    featured?: boolean;
}

function GalleryCard({ item, onView }: { item: GalleryItem; onView: (item: GalleryItem) => void }) {
    return (
        <article className="flex flex-col rounded-[8px] bg-[#f5f5f5] p-4">
            <button
                type="button"
                onClick={() => onView(item)}
                aria-label={`${item.kind === 'video' ? 'Watch' : 'View'} ${item.title}`}
                className="group relative h-[170px] w-full overflow-hidden rounded-[4px] bg-[#d9d9d9]"
            >
                <img
                    src={item.image}
                    alt=""
                    className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                />
                {item.kind === 'video' && (
                    <>
                        <span className="absolute inset-0 bg-black/25" />
                        <span className="absolute left-1/2 top-1/2 flex size-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-[#08080c] transition-transform duration-300 group-hover:scale-[1.1]">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                                <path d="M8 5.5v13l11-6.5-11-6.5Z" />
                            </svg>
                        </span>
                        {item.duration && (
                            <span className="absolute bottom-2 right-2 rounded-[4px] bg-black/70 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                                {item.duration}
                            </span>
                        )}
                    </>
                )}
            </button>

            <p className="mt-4 text-[13px] font-semibold leading-[1.6] text-[#08080c]">{item.title}</p>
            {item.caption && <p className="mt-1.5 text-[12px] leading-[1.6] text-[#5e5e5e]">{item.caption}</p>}

            <div className="mt-auto">
                <CardActions
                    title={item.title}
                    shareUrl={absoluteUrl(`/resources/gallery#${item.slug}`)}
                    onView={() => onView(item)}
                    viewLabel={item.kind === 'video' ? 'Watch' : 'View'}
                />
            </div>
        </article>
    );
}

/**
 * The gallery grid, with its own Videos / Images switch. Gallery items are
 * viewed and shared rather than downloaded, so the cards carry no download.
 */
export function GalleryGrid({ items }: { items: GalleryItem[] }) {
    const [kind, setKind] = useState<'video' | 'image'>('video');
    const [preview, setPreview] = useState<LightboxContent | null>(null);

    const shown = items.filter((i) => i.kind === kind);
    const tabs: { id: 'video' | 'image'; label: string }[] = [
        { id: 'video', label: 'Videos' },
        { id: 'image', label: 'Images' },
    ];

    return (
        <>
            <div role="tablist" aria-label="Gallery media" className="mt-6 flex items-center gap-2">
                {tabs.map((t) => {
                    const active = kind === t.id;
                    const count = items.filter((i) => i.kind === t.id).length;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setKind(t.id)}
                            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-bold transition-colors ${
                                active
                                    ? 'bg-[#08080c] text-white'
                                    : 'border border-[#e2e2e2] bg-white text-[#5e5e5e] hover:border-[#08080c] hover:text-[#08080c]'
                            }`}
                        >
                            {t.label}
                            <span className={active ? 'text-white/60' : 'text-[#9a9a9a]'}>{count}</span>
                        </button>
                    );
                })}
            </div>

            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {shown.map((item) => (
                    <GalleryCard
                        key={item.slug}
                        item={item}
                        onView={(i) =>
                            setPreview({
                                title: i.title,
                                image: i.image,
                                videoUrl: i.videoUrl,
                                caption: i.caption,
                            })
                        }
                    />
                ))}
            </div>

            <Lightbox item={preview} onClose={() => setPreview(null)} />
        </>
    );
}
